// data.ts — production data source for the rune-icon bundle.
//
// PHP emits `window.FT_RUNE = { layerVisual, statusMeta, iconCdn, itemBase }` once
// per page (see ft_emit_rune_data_global() in includes/functions/). The bundle reads
// it here at module-eval, so FT_RUNE MUST be injected BEFORE the bundle <script>.
// If it is missing the bundle degrades gracefully: no generic-token tier (empty
// LAYER_VISUAL) and status icons fall back to their base name (empty STATUS_META).

import type { LayerVisual, StatusMeta } from './types';

interface FtRune {
    layerVisual?: Record<string, LayerVisual>;
    statusMeta?: Record<string, StatusMeta>;
    iconCdn?: string;
    itemBase?: string;
}

type RuneDataWindow = Window & { FT_RUNE?: unknown };

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function layerVisualMap(value: unknown): Record<string, LayerVisual> {
    const rows = recordOf(value);
    if (!rows) return {};
    // Normalize lazily: production sends a plain JSON object, while the exhaustive smoke deliberately
    // supplies a Proxy that classifies every requested layer. Reading through preserves both shapes.
    return new Proxy<Record<string, LayerVisual>>({}, {
        get(_target, key): LayerVisual | undefined {
            if (typeof key !== 'string') return undefined;
            const row = recordOf(rows[key]);
            return row && typeof row.f === 'string' && typeof row.k === 'string'
                && typeof row.m === 'number' && typeof row.c === 'number'
                ? { f: row.f, k: row.k, m: row.m, c: row.c }
                : undefined;
        },
    });
}

function statusMetaMap(value: unknown): Record<string, StatusMeta> {
    const out: Record<string, StatusMeta> = {};
    const rows = recordOf(value);
    if (!rows) return out;
    for (const [key, value] of Object.entries(rows)) {
        const row = recordOf(value);
        if (row && typeof row.v === 'string' && typeof row.b === 'number') {
            out[key] = { v: row.v, b: row.b };
        }
    }
    return out;
}

const raw = recordOf((window as RuneDataWindow).FT_RUNE);
const FT: FtRune = {
    layerVisual: layerVisualMap(raw?.layerVisual),
    statusMeta: statusMetaMap(raw?.statusMeta),
    iconCdn: typeof raw?.iconCdn === 'string' ? raw.iconCdn : undefined,
    itemBase: typeof raw?.itemBase === 'string' ? raw.itemBase : undefined,
};

export const LAYER_VISUAL: Record<string, LayerVisual> = FT.layerVisual || {};
export const STATUS_META: Record<string, StatusMeta> = FT.statusMeta || {};
export const ICON_CDN: string = FT.iconCdn || 'https://cdn.fatetide.com/images/icons/';
export const ITEM_BASE: string = FT.itemBase || '';
